import { objectEntries } from "app/helpers/objects";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import {
    ancestorIdsOf,
    collectAncestorsOfChecked,
    isCoveredBy,
} from "app/pages/sales-intelligence/utils/taxonomy-tree/tree";

import { INDUSTRY_SUGGESTIONS_LIMIT_INDEX } from "../constants/suggestions";
import type { IndustryAffinity, IndustryAffinityDto, IndustryAffinityIndex } from "../types/affinity";
import type { IndustrySuggestion } from "../types/suggestions";
import type { IndustryNodeId, IndustryTaxonomyIndex } from "../types/taxonomy";

const pathCollator = new Intl.Collator("en", { sensitivity: "base" });

const isValidScore = (score: number): boolean => Number.isFinite(score) && score >= 0 && score <= 1;

/** Normalises the wire config; malformed pairs are dropped rather than failing the whole row. */
export const indexAffinity = (dto: IndustryAffinityDto): IndustryAffinityIndex => {
    const index = new Map<IndustryNodeId, readonly IndustryAffinity[]>();

    for (const [source, targets] of objectEntries(dto.affinities)) {
        const bestById = new Map<IndustryNodeId, number>();

        for (const { id, score } of targets) {
            if (id !== source && isValidScore(score)) {
                bestById.set(id, Math.max(bestById.get(id) ?? score, score));
            }
        }

        index.set(
            source,
            [...bestById].map(([id, score]) => ({ id, score })),
        );
    }

    return index;
};

const toSuggestion = (id: IndustryNodeId, score: number, taxonomy: IndustryTaxonomyIndex): IndustrySuggestion => {
    const nodeName = (nodeId: IndustryNodeId) => taxonomy.byId.get(nodeId)?.name ?? "";
    const pathIds = [...ancestorIdsOf(taxonomy, id)].reverse().concat(id);

    return {
        id,
        label: nodeName(id),
        fullPath: formatTaxonomyPathDisplayNames(pathIds.map(nodeName)),
        score,
    };
};

const byAffinityThenPath = (a: IndustrySuggestion, b: IndustrySuggestion): number =>
    b.score - a.score || pathCollator.compare(a.fullPath, b.fullPath) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const leafNameKey = (suggestion: IndustrySuggestion): string => suggestion.label.trim().toLocaleLowerCase();

type RankParams = {
    readonly values: readonly IndustryNodeId[];
    readonly taxonomy: IndustryTaxonomyIndex;
    readonly affinity: IndustryAffinityIndex;
    readonly dismissed: ReadonlySet<IndustryNodeId>;
};

/**
 * Every node worth suggesting for a selection, best first. A candidate scores its strongest affinity
 * to any selected node, so one total order exists for backfilling a dismissed slot. Ancestors of the
 * selection are never offered: adding one would absorb the user's own narrower pick.
 */
export const rankIndustryAffinitySuggestions = ({
    values,
    taxonomy,
    affinity,
    dismissed,
}: RankParams): readonly IndustrySuggestion[] => {
    const sources = values.filter((id) => taxonomy.byId.has(id));
    const ancestorsOfSelection = collectAncestorsOfChecked(sources, taxonomy);
    const bestScoreById = new Map<IndustryNodeId, number>();

    for (const source of sources) {
        for (const { id, score } of affinity.get(source) ?? []) {
            const isEligible =
                taxonomy.byId.has(id) && !ancestorsOfSelection.has(id) && !isCoveredBy(sources, id, taxonomy);

            if (isEligible) {
                bestScoreById.set(id, Math.max(bestScoreById.get(id) ?? score, score));
            }
        }
    }

    const ranked = [...bestScoreById].map(([id, score]) => toSuggestion(id, score, taxonomy)).sort(byAffinityThenPath);

    // Collapsed before dismissals apply, so dismissing a name never resurfaces its weaker namesake.
    const seenLeafNames = new Set<string>();
    const onePerLeafName = ranked.filter((suggestion) => {
        const key = leafNameKey(suggestion);

        if (seenLeafNames.has(key)) {
            return false;
        }

        seenLeafNames.add(key);

        return true;
    });

    return onePerLeafName.filter((suggestion) => !dismissed.has(suggestion.id));
};

/** The suggestion that takes a freed slot once `nextPool` replaces what is on screen. */
export const nextSuggestionReplacement = (
    nextPool: readonly IndustrySuggestion[],
    visibleIds: readonly IndustryNodeId[],
): IndustryNodeId | undefined =>
    nextPool.slice(0, INDUSTRY_SUGGESTIONS_LIMIT_INDEX).find((suggestion) => !visibleIds.includes(suggestion.id))?.id;
